import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNotification } from '../../../contexts/NotificationContext';
import { contentService } from '../../../services/api';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h2`
  color: #1f2937;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
`;

const Description = styled.p`
  color: #6b7280;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Section = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3`
  color: #1f2937;
  margin-bottom: 1rem;
  font-size: 1.125rem;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
`;

const ToggleSlider = styled.span<{ $checked: boolean }>`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ $checked }) => $checked ? '#10b981' : '#d1d5db'};
  transition: 0.4s;
  border-radius: 24px;
  
  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: ${({ $checked }) => $checked ? '26px' : '3px'};
    bottom: 3px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
`;

const MapPreview = styled.div`
  margin-top: 0.5rem;
  height: 200px;
  background: #f3f4f6;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-start;
  margin-top: 2rem;
`;

const SaveButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #2563eb;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreviewButton = styled.button`
  background: #6b7280;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #4b5563;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
`;

const HelpText = styled.small`
  color: #6b7280;
  margin-top: 0.25rem;
  font-size: 0.875rem;
`;

const ContactSettings: React.FC = () => {
  const [formData, setFormData] = useState({
    contact_title: '',
    contact_subtitle: '',
    contact_address_full: '',
    contact_hours: '',
    contact_map_enabled: true,
    contact_map_latitude: '',
    contact_map_longitude: '',
    google_maps_api_key: '',
    phone: '',
    email: '',
    whatsapp_number: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await contentService.getSiteSettings();
      
      setFormData({
        contact_title: response.contact_title || 'Entre em Contato',
        contact_subtitle: response.contact_subtitle || '',
        contact_address_full: response.contact_address_full || '',
        contact_hours: response.contact_hours || '',
        contact_map_enabled: response.contact_map_enabled !== false,
        contact_map_latitude: response.contact_map_latitude ?? '',
        contact_map_longitude: response.contact_map_longitude ?? '',
        google_maps_api_key: response.google_maps_api_key || '',
        phone: response.phone || '',
        email: response.email || '',
        whatsapp_number: response.whatsapp_number || ''
      });
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      showNotification('Erro ao carregar configurações da página de contato', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      await contentService.updateSiteSettings({
        ...formData,
        contact_map_latitude: formData.contact_map_latitude === '' ? null : Number(formData.contact_map_latitude),
        contact_map_longitude: formData.contact_map_longitude === '' ? null : Number(formData.contact_map_longitude),
      });
      
      showNotification('Configurações da página de contato salvas com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      showNotification('Erro ao salvar configurações da página de contato', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    window.open('/contato', '_blank');
  };

  if (loading) {
    return <LoadingMessage>Carregando configurações...</LoadingMessage>;
  }

  return (
    <Container>
      <Title>Configuração da Página de Contato</Title>
      <Description>
        Configure as informações de contato e o mapa da sua imobiliária. 
        Essas informações serão exibidas na página de contato e em outros locais do site.
      </Description>

      <Form onSubmit={handleSave}>
        <Section>
          <SectionTitle>Informações da Página</SectionTitle>
          
          <FormGroup>
            <Label>Título da Página</Label>
            <Input
              type="text"
              value={formData.contact_title}
              onChange={(e) => handleInputChange('contact_title', e.target.value)}
              placeholder="Ex: Entre em Contato"
            />
          </FormGroup>

          <FormGroup>
            <Label>Subtítulo</Label>
            <TextArea
              value={formData.contact_subtitle}
              onChange={(e) => handleInputChange('contact_subtitle', e.target.value)}
              placeholder="Ex: Estamos aqui para ajudar você a encontrar o imóvel perfeito..."
              rows={2}
            />
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle>Informações de Contato</SectionTitle>
          
          <FormRow>
            <FormGroup>
              <Label>Telefone</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="(11) 99999-9999"
              />
            </FormGroup>

            <FormGroup>
              <Label>E-mail</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="contato@imobiliaria.com"
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>WhatsApp (apenas números)</Label>
            <Input
              type="text"
              value={formData.whatsapp_number}
              onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
              placeholder="5511999999999"
            />
            <HelpText>Formato: Código do país + DDD + número (ex: 5511999999999)</HelpText>
          </FormGroup>

          <FormGroup>
            <Label>Endereço Completo</Label>
            <TextArea
              value={formData.contact_address_full}
              onChange={(e) => handleInputChange('contact_address_full', e.target.value)}
              placeholder="Rua das Flores, 123, Centro, São Paulo - SP, 01234-567"
              rows={2}
            />
          </FormGroup>

          <FormGroup>
            <Label>Horário de Funcionamento</Label>
            <TextArea
              value={formData.contact_hours}
              onChange={(e) => handleInputChange('contact_hours', e.target.value)}
              placeholder="Segunda a Sexta: 8h às 18h&#10;Sábado: 8h às 12h&#10;Domingo: Fechado"
              rows={3}
            />
            <HelpText>Use quebras de linha para separar os dias</HelpText>
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle>Configuração do Mapa</SectionTitle>
          
          <FormGroup>
            <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Exibir Mapa
              <ToggleSwitch>
                <ToggleInput
                  type="checkbox"
                  checked={formData.contact_map_enabled}
                  onChange={(e) => handleInputChange('contact_map_enabled', e.target.checked)}
                />
                <ToggleSlider $checked={formData.contact_map_enabled} />
              </ToggleSwitch>
            </Label>
          </FormGroup>

          {formData.contact_map_enabled && (
            <>
              <FormGroup>
                <Label>Chave da API do Google Maps</Label>
                <Input
                  type="text"
                  value={formData.google_maps_api_key}
                  onChange={(e) => handleInputChange('google_maps_api_key', e.target.value)}
                  placeholder="Sua chave da API do Google Maps"
                />
                <HelpText>
                  Obtenha gratuitamente em: 
                  <a href="https://developers.google.com/maps/gmp-get-started" target="_blank" rel="noopener noreferrer">
                    {' '}Google Cloud Platform
                  </a>
                </HelpText>
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    min="-90"
                    max="90"
                    step="any"
                    value={formData.contact_map_latitude}
                    onChange={(e) => handleInputChange('contact_map_latitude', e.target.value)}
                    placeholder="Latitude do endereço"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    min="-180"
                    max="180"
                    step="any"
                    value={formData.contact_map_longitude}
                    onChange={(e) => handleInputChange('contact_map_longitude', e.target.value)}
                    placeholder="Longitude do endereço"
                  />
                </FormGroup>
              </FormRow>

              <HelpText>
                Dica: Use o Google Maps para encontrar as coordenadas exatas do seu endereço.
                Clique com o botão direito no local desejado e selecione as coordenadas.
              </HelpText>

              <MapPreview>
                {formData.google_maps_api_key ? (
                  `Mapa será exibido em: ${formData.contact_map_latitude}, ${formData.contact_map_longitude}`
                ) : (
                  'Configure a chave da API para visualizar o mapa'
                )}
              </MapPreview>
            </>
          )}
        </Section>

        <ButtonGroup>
          <SaveButton type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </SaveButton>
          <PreviewButton type="button" onClick={handlePreview}>
            Visualizar Página
          </PreviewButton>
        </ButtonGroup>
      </Form>
    </Container>
  );
};

export default ContactSettings;
